<template>
  <div :class="$style['notes-container']" data-qa-id="submit-drawer-notes">
    <div :class="$style['section']">
      <div :class="$style['upload-notes-header']" data-qa-id="notes-upload-header">
        <MdTypography tag="h7" variant="heading7--medium">
          {{ $t('pages.inventory_ingestion_hub.submit_drawer.notes.upload_notes') }}
        </MdTypography>
      </div>
      <div :class="$style['upload-notes-text']" data-qa-id="notes-upload-text">
        <MdTypography v-if="uploadNotes" tag="p" variant="body2--regular" color="spirits--700" v-html="uploadNotes" data-qa-id="notes-upload-html" />
        <MdTypography v-else tag="p" variant="body2--regular" color="spirits--700" data-qa-id="notes-upload-empty">
          {{ $t('pages.inventory_ingestion_hub.submit_drawer.no_results.no_upload_notes') }}
        </MdTypography>
      </div>
    </div>

    <div :class="$style['section']">
      <div :class="$style['family-notes-header']" data-qa-id="family-notes-header">
        <div :class="$style['family-notes-header-title']" data-qa-id="family-notes-title">
          <MdTypography tag="h7" variant="heading7--medium">
            {{ $t('pages.inventory_ingestion_hub.submit_drawer.notes.family_notes') }}
          </MdTypography>
        </div>
        <div :class="$style['family-notes-header-search']" data-qa-id="family-notes-search">
          <TextField
            id="notes_family_search_input"
            v-model="search"
            :searchEnable="true"
            :showCrossIcon="true"
            :placeholder="$t('pages.inventory_ingestion_hub.family_search_placeholder')"
            :onSearchCrossIconClick="clearSearch"
            :input-class="$style['family-notes-search-input']"
          />
        </div>
      </div>

      <div :class="$style['family-list']" data-qa-id="family-notes-list">
        <div v-for="(family, index) in filteredFamilies" :key="index" :class="$style['family-item']" :data-qa-id="`family-note-${index}`">
          <MdTypography tag="div" variant="body2--bold" color="spirits--900" data-qa-id="family-name">
            {{ family?.name }}
          </MdTypography>
          <MdTypography
            v-if="family?.note"
            tag="div"
            variant="body2--regular"
            color="spirits--700"
            data-qa-id="family-note-html"
            v-html="family?.note"
          />
          <MdTypography v-else tag="div" variant="body2--regular" color="spirits--700" data-qa-id="family-note-empty">
            {{ $t('pages.inventory_ingestion_hub.submit_drawer.no_results.no_family_notes') }}
          </MdTypography>
        </div>

        <div v-if="filteredFamilies.length === 0" :class="$style['no-results']" data-qa-id="family-notes-no-results">
          <NoResultFound
            :noResultsTitle="$t('pages.inventory_ingestion_hub.submit_drawer.no_results.title')"
            :noResultsContent="$t('pages.inventory_ingestion_hub.submit_drawer.no_results.content')"
            :iconToShow="NoResultImage"
            :imgHeight="240"
            :imgWidth="240"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { MdTypography } from '@antiqua-dls/vue-components';
import TextField from 'src/components/form/new-text-field/index.vue';
import i18n from 'src/assets/i18n/i18n';
import NoResultImage from 'src/components/icons/lady-thinking-icon/no_result_image.svg';
import NoResultFound from 'src/components/general/no-result-found/index.vue';

export default {
  name: 'Notes',

  components: {
    MdTypography,
    TextField,
    NoResultFound
  },

  props: {
    uploadNotes: {
      type: String,
      default: ''
    },
    familyNotes: {
      type: Array,
      default: () => []
    }
  },

  setup(props) {
    const $t = i18n().global.t;
    const search = ref('');

    const filteredFamilies = computed(() => {
      const term = search?.value?.toLowerCase().trim();
      if (!term) return props.familyNotes;
      return props.familyNotes.filter(f => (f?.name || '').toLowerCase().includes(term));
    });

    const clearSearch = () => {
      search.value = '';
    };

    return {
      $t,
      search,
      filteredFamilies,
      clearSearch,
      NoResultImage
    };
  }
};
</script>

<style module lang="scss">
.notes-container {
  display: flex;
  flex-direction: column;

  .section {
    display: flex;
    flex-direction: column;

    .upload-notes-header {
      padding: var(--md-spacing-unit--sm) var(--md-spacing-unit--2xl);
      border-bottom: 1px solid var(--md-color--spirits--300);
    }

    .upload-notes-text {
      padding: var(--md-spacing-unit--md) var(--md-spacing-unit--2xl);
    }

    .family-notes-header {
      display: flex;
      align-items: center;
      gap: var(--md-spacing-unit--md);
      border-top: 1px solid var(--md-color--spirits--300);
      border-bottom: 1px solid var(--md-color--spirits--300);

      .family-notes-header-title {
        width: 50%;
        padding-left: var(--md-spacing-unit--2xl);
      }

      .family-notes-header-search {
        border-left: 1px solid var(--md-color--spirits--300);
        width: 54%;
        .family-notes-search-input {
          gap: var(--md-spacing-unit--xs);
          border: none;
          border-radius: 0;
          opacity: 1;
          padding: 1.25rem var(--md-spacing-unit--lg);

          input {
            width: 100%;
            height: 100%;
            border-radius: var(--md-spacing-unit--xs);
            margin-bottom: 0;
            padding-left: 0 !important;

            &::placeholder {
              font-family: var(--md-font-family--text-regular);
              font-size: var(--md-font-size--xs);
              color: var(--md-color--spirits--600);
            }
          }
        }
      }
    }

    .family-list {
      padding: var(--md-spacing-unit--md) 0;
      gap: var(--md-spacing-unit--md);
      display: flex;
      flex-direction: column;
      .family-item {
        display: flex;
        flex-direction: column;
        gap: var(--md-spacing-unit--xs);
        padding: var(--md-spacing-unit--xs) var(--md-spacing-unit--2xl);
      }
    }
  }
}
</style>
